import { createAsyncThunk } from '@reduxjs/toolkit';
import { uniq } from 'ramda';
import { MOSCOW_REGION_ID } from 'shared/constants/regions';
import {
  fetchApiGeoGetRoads,
  TApiGeoGetRoadsResponse,
} from 'shared/repositories/monolith-cian-realty/api/geo/get-roads';
import { selectDirectionsModalData } from 'shared/selectors/geo';
import { selectJsonQuery } from 'shared/selectors/jsonQuery';
import { selectLocation } from 'shared/selectors/location';
import { updateGeo } from 'shared/slices/jsonQuery';
import { TLocation } from 'shared/types/location';
import { IThunkApi } from 'shared/types/redux';
import { updateSearchMeta } from '../../searchMeta';
import { addHighwaysInJsonQuery, getHighwaysFromJsonQuery } from 'shared/utils/jsonQuery';
import { setDirections } from 'shared/slices/tags';
import { IDirection } from 'shared/types/geo';

interface IThunkApiExtended extends IThunkApi {
  rejectValue: void;
}

interface IParam {
  highwaysIds: number[];
  regionId?: number;
}

export const selectHighways = createAsyncThunk<void, IParam, IThunkApiExtended>(
  'geo/directionsModal/selectHighways',
  async ({ highwaysIds, regionId }, { getState, dispatch, extra: context }) => {
    const state = getState();
    const currentLocation = selectLocation(state);
    const directionsModal = selectDirectionsModalData(state);
    const jsonQuery = selectJsonQuery(state);

    const curRegionId = getRegionId(currentLocation);
    const directionRegionId = regionId || curRegionId;

    const directionsPromise =
      directionsModal.length && (!regionId || regionId === curRegionId)
        ? Promise.resolve(null)
        : fetchApiGeoGetRoads<TApiGeoGetRoadsResponse>({
            httpApi: context.httpApi,
            parameters: { regionId: directionRegionId },
            config: {
              subdomain: context.custom.subdomain,
            },
          });

    try {
      const result = await directionsPromise;

      if (result) {
        const directions = result.response as IDirection[];

        dispatch(setDirections(directions));
      }
    } catch (e) {
      context.logger.error(new Error(`Failed to get roads for regionId = ${regionId}`));
    }

    const selectedHighwaysIds = getHighwaysFromJsonQuery(jsonQuery.geo);

    // All highways ids already a selected
    if (highwaysIds.every(highwayId => selectedHighwaysIds.includes(highwayId))) {
      return;
    }

    const newSelectedHighways = uniq<number>(selectedHighwaysIds.concat(highwaysIds));

    const newGeo = addHighwaysInJsonQuery(jsonQuery, newSelectedHighways);

    dispatch(updateGeo(newGeo));
    await dispatch(updateSearchMeta());
  },
);

function getRegionId(location: TLocation): number {
  if (location === 'moscow_mo') {
    return MOSCOW_REGION_ID;
  }

  if (location === 'spb_lo') {
    return -2;
  }

  return location.id;
}
